import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'What is a payout ratio?',
    a: 'The payout ratio is the percentage of a company\u2019s net earnings paid out as dividends. The formula is simple: Dividends Paid \u00f7 Net Income \u00d7 100. A company that earns $10 billion and pays $5 billion in dividends has a 50% payout ratio. It is the single best one-glance answer to the question "can this company keep paying me?".',
  },
  {
    q: 'What is a "safe" payout ratio?',
    a: 'For most industrial companies, anything in the 35\u201355% range is healthy. Under 40% is conservative and leaves room for raises. 60\u201380% is moderate risk \u2014 the company has less cushion. Above 80% is elevated risk; above 100% means the company is paying out more than it earns and is borrowing or burning cash to fund the dividend. Sector context matters \u2014 see below.',
  },
  {
    q: 'Do REITs and utilities have different rules?',
    a: 'Yes. REITs are legally required to distribute at least 90% of their taxable income to retain their tax-advantaged status, so payout ratios of 70\u201395% are normal. For REITs, the better metric is FFO payout (Funds From Operations) or AFFO payout, which adjusts for depreciation. Utilities run 55\u201375% comfortably because their cash flows are regulated and stable. MLPs publish a Distributable Cash Flow (DCF) coverage ratio in place of payout ratio.',
  },
  {
    q: 'Why is high payout ratio dangerous?',
    a: 'When earnings dip in a recession, a 90% payout ratio leaves no room \u2014 management has to choose between cutting the dividend or borrowing to fund it. Most historical dividend cuts (Kraft Heinz 2019, AT&T 2022, Walgreens 2024, Intel 2023) were preceded by payout ratios above 80% for years. The metric is forward-looking in a way headline yield is not.',
  },
  {
    q: 'Does DiviDrip show me the payout ratio?',
    a: 'Yes. Open any ticker on the Dashboard, and the Stock Modal\u2019s Dividend Info tab displays the current Payout Ratio with color coding (green / amber / red) so you can see at a glance whether the dividend has cushion or is stretched. The same metric is one of the inputs into the Dividend Triangle score on the Triangle tab.',
  },
];

export default function LearnPayoutRatio() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title="The Payout Ratio Explained — The Single Most Important Dividend Metric (2026) | DiviDrip"
      description="If you check one dividend safety metric before buying, make it the payout ratio. The formula, the safe ranges, sector exceptions, and what triggers most dividend cuts."
      canonical="https://twylightcrow.com/learn/payout-ratio"
      guidePath="/learn/payout-ratio"
      sectionLabel="Metrics & Evaluation"
      sectionAnchor="metrics"
      kicker="Learn · Dividend safety"
      h1="The Payout Ratio — Your First Dividend Safety Check"
      datePublished="2026-05-24"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/five-numbers-to-check', label: 'The 5 numbers I check before buying any dividend stock' },
        { to: '/learn/dividend-traps', label: 'Dividend trap stocks — spotting yields that lie' },
        { to: '/learn/dividend-triangle', label: 'Reading the Dividend Triangle score' },
        { to: '/learn/yield-on-cost', label: 'Yield on cost — your real long-term yield' },
        { to: '/learn/reits-vs-bdcs-vs-cefs', label: 'REITs vs BDCs vs CEFs — high-yield vehicles' },
        { to: '/learn/mlps-explained', label: 'MLPs explained — distribution coverage and K-1s' },
        { to: '/glossary', label: 'Glossary — every dividend term defined' },
      ]}
    >
      <p>
        If you could only check one dividend-safety metric before buying a stock,
        <strong> payout ratio</strong> is the one to pick. It answers the most
        important question a dividend investor can ask: <em>how much room does this
        company have to keep paying me — and to raise the dividend from here?</em>
      </p>
      <p>
        It is also the metric most beginners ignore. Headline yield is loud and easy.
        Payout ratio requires a second click. That gap is exactly why bad dividend buys
        keep happening.
      </p>

      <h2>The formula</h2>
      <p>
        <code>Payout Ratio = (Dividends Paid &divide; Net Income) &times; 100</code>
      </p>
      <p>Worked example:</p>
      <ul>
        <li>Company earns <strong>$10 billion</strong> in net income for the year.</li>
        <li>It pays out <strong>$5 billion</strong> in dividends.</li>
        <li>Payout ratio = 5 &divide; 10 &times; 100 = <strong>50%</strong>.</li>
      </ul>
      <p>
        Half of every dollar earned was returned to shareholders. The other half stayed
        in the business — to fund growth, pay down debt, buy back shares, or build a
        cushion for lean years. That is exactly the kind of room you want a dividend
        payer to have.
      </p>

      <h2>The safety ladder</h2>
      <table className="learn-table">
        <thead>
          <tr>
            <th>Payout Ratio</th>
            <th>Interpretation</th>
            <th>What it tells you</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Under 40%</td>
            <td>Conservative</td>
            <td>Lots of room. Future raises likely. Lower current yield is typical.</td>
          </tr>
          <tr>
            <td>40 – 60%</td>
            <td>Healthy</td>
            <td>The sweet spot for most industrial dividend growers (KO, PG, JNJ).</td>
          </tr>
          <tr>
            <td>60 – 80%</td>
            <td>Moderate risk</td>
            <td>Less cushion. A bad year tightens management&apos;s choices.</td>
          </tr>
          <tr>
            <td>80 – 100%</td>
            <td>Elevated risk</td>
            <td>One earnings miss away from being stretched. Watch closely.</td>
          </tr>
          <tr>
            <td>Above 100%</td>
            <td>Unsustainable (signal)</td>
            <td>Paying out more than they earn. Funded by debt, asset sales, or cash drawdowns. Cuts often follow.</td>
          </tr>
        </tbody>
      </table>
      <p>
        Most analysts and dividend-focused fund managers consider <strong>35 – 55%</strong>{' '}
        the strongest long-term band for traditional sectors. That range pays a meaningful
        dividend, leaves room for raises, and survives recessions without forcing a cut.
      </p>

      <h2>Sector exceptions — read this before judging a REIT</h2>
      <p>
        Some industries naturally operate with higher payout ratios because of how they
        are structured. Applying the &quot;under 60% is healthy&quot; rule to a REIT will
        cause you to wrongly disqualify the entire sector.
      </p>
      <ul>
        <li>
          <strong>REITs.</strong> Required by US tax law to distribute at least 90% of
          taxable income to keep their REIT status. Headline payout ratios of 75 – 95%
          are <em>normal</em> and not a red flag by themselves. Use{' '}
          <strong>FFO payout</strong> or <strong>AFFO payout</strong> instead — those
          adjust for the heavy non-cash depreciation REITs report. An AFFO payout under
          80% is comfortable for an equity REIT. See{' '}
          <Link to="/learn/reits-vs-bdcs-vs-cefs">REITs vs BDCs vs CEFs</Link> for the
          full structure.
        </li>
        <li>
          <strong>Utilities.</strong> Regulated cash flows are stable and predictable, so
          utilities can sustain 55 – 75% payouts that would be risky elsewhere. Above 80%
          starts to matter; SO and DUK have lived comfortably in the 65 – 75% band for
          decades.
        </li>
        <li>
          <strong>MLPs.</strong> Energy partnerships don&apos;t report a traditional
          payout ratio — they publish <strong>Distributable Cash Flow (DCF) coverage</strong>.
          Aim for DCF coverage of 1.2x or higher (meaning the partnership earned 20% more
          cash than it distributed). See{' '}
          <Link to="/learn/mlps-explained">MLPs explained</Link>.
        </li>
        <li>
          <strong>BDCs.</strong> Like REITs, BDCs are required to distribute &ge;90% of
          taxable income. Use Net Investment Income (NII) coverage instead — 100% NII
          coverage means the BDC earned its distribution; below 100% means it dipped into
          spillover income or capital.
        </li>
      </ul>

      <h2>Why investors ignore this metric</h2>
      <p>
        Two reasons, both lazy:
      </p>
      <ol>
        <li>
          <strong>Yield is loud.</strong> An 11% headline yield generates clicks and
          excitement. A 56% payout ratio doesn&apos;t. So the screener sorts by yield,
          the eye lands on the biggest number, and the payout ratio never gets opened.
        </li>
        <li>
          <strong>It requires sector context.</strong> A new investor sees a 92% payout
          ratio on a REIT and panics. A new investor sees a 65% payout ratio on a tech
          company and assumes it&apos;s fine. Both reactions are wrong. Payout ratio
          rewards a few minutes of category-by-category learning.
        </li>
      </ol>
      <p>
        The cost of ignoring it shows up in the dividend-cut history. Kraft Heinz cut
        50% in 2019 after years above 90% payout. AT&amp;T cut 47% in 2022 after a 100%+
        payout stretch. Walgreens cut 48% in 2024 after a 90%+ run. Intel suspended in
        2023 after the same pattern. In every case, the payout ratio was screaming
        for years — and most retail holders only noticed when the cut hit the news.
      </p>

      <h2>How to use this in a 30-second buy check</h2>
      <ol>
        <li>Open the ticker&apos;s Stock Modal on DiviDrip.</li>
        <li>Look at the <strong>Payout Ratio</strong> tile on the Dividend Info tab.</li>
        <li>
          Compare against the sector band above. Under 60% for an industrial blue chip?
          Green. Above 80% for a tech company? Open a tab and check the trend over the
          last 5 years. Above 100% anywhere? You need a reason — usually a temporary
          earnings dip — before this is a buy.
        </li>
        <li>
          Cross-reference with the{' '}
          <Link to="/learn/dividend-triangle">Dividend Triangle score</Link> — payout
          ratio is one of the inputs, so the Triangle is your single-glance answer if
          you don&apos;t want to think about it manually.
        </li>
      </ol>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <h2>Try it</h2>
      <p>
        Open <Link to="/dashboard">DiviDrip</Link>, search any stock you&apos;ve been
        eyeing, and jump to its <strong>Dividend Info</strong> tab. The Payout Ratio is
        color-coded green / amber / red based on the bands above (with sector
        adjustments applied automatically for REITs, BDCs, and MLPs). If it&apos;s red
        and you weren&apos;t aware, you just dodged a likely cut candidate.
      </p>
    </LearnPage>
  );
}
